"use client";

// DirectoryView — the folder/page grid for a level. Three ways to look at the
// same children, remembered per level:
//   • free  — folders resting over the organism, ARRANGED BY THE ROOM, not by
//     hand. See the layout law below: positions are computed, never stored, so
//     nothing can ever come to rest on top of anything else.
//   • grid  — auto-arranged, scrollable (the default on mobile). Tiles lift into
//     a pointer-following ghost past a small slop; the same dwell-to-confirm
//     glow moves them into folders. The grid never reflows during a drag.
//   • list  — dense rows when you know what you're looking for (click-only).
// SELECT vs OPEN: a single tap SELECTS (the compact 2px rect); a double tap OPENS.
// A double-click on a NAME edits it in place (the inline-edit law) — grid/list/
// orbit. (Free-scatter names stay read-only for now: the drag gesture and inline
// editing would fight; rename there from grid/list.)
//
// ── THE LAYOUT LAW (free view) ──────────────────────────────────────────────
// Folders on home are NOT draggable to a position, and a position is never
// persisted. Hand-placing was a standing bug factory: a fraction saved on one
// screen, replayed on another (or written NaN by a cancelled gesture), landed
// tiles in a heap in the top-left corner, and the spiral separator that was
// supposed to catch it ran on positions that had already collapsed.
//
// So the room arranges itself. `packLayout` walks the children into a cellular
// grid sized from the stage, then offsets each tile inside ITS OWN cell by a
// deterministic per-key jitter — organic to look at, provably collision-free
// (an offset can never reach past its cell's half-clearance, so two tiles cannot
// approach each other closer than the clearance). It recomputes on resize, so
// the arrangement is always right for the screen it's actually on.
//
// Dragging survives, but ONLY as a move: pick a folder up, dwell it over
// another, release, and it goes inside — landing live, with a pulse on the
// folder that received it, no refresh to find out where things went. A release
// anywhere else simply glides the tile home to its computed spot.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDrag } from "@use-gesture/react";
import { relTime, type OsNode } from "../../lib/os/tree";
import { type LayoutState } from "../../lib/os/useOsLayout";
import { useDropConfirm } from "../../lib/os/useDropConfirm";
import { packLayout } from "../../lib/os/packLayout";
import { stageViewport } from "../../lib/stage/scroller";
import { useGlow } from "../useGlow";
import { motion, useHoverFeel, useReducedMotion } from "../../lib/motion";
import { DEFAULT_SORT, sortNodes, type FolderSort } from "../../lib/os/folderSort";
import DirIcon, { DirIconScaleStyle } from "./DirIcon";
import { dirMark } from "./dirMark";
import { EditableName } from "./EditableText";
import ContextMenu, { useContextMenu, type MenuItem } from "./ContextMenu";
import CellAvatar from "../CellAvatar";
import RepoMark from "./RepoMark";
import { useOsShell } from "./OsShellContext";
import BatchForgetSheet, { type BatchForgetTarget } from "./BatchForgetSheet";
import { PillButton } from "../ui";

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// THE HOVER IS THE TRACKER'S. The one HoverField (components/HoverField) marks
// the element under the pointer with `data-hl` and clears it the moment the
// pointer is no longer truly there — on scroll, on a glide that carries the tile
// away, on blur, on unmount. A tile's own pointerenter/leave missed all of those
// and left a folder lit after the mouse had gone. So a tile only READS the mark.
function useHl(ref: React.RefObject<HTMLElement | null>): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setOn(el.hasAttribute("data-hl"));
    read();
    const mo = new MutationObserver(read);
    mo.observe(el, { attributes: true, attributeFilter: ["data-hl"] });
    return () => mo.disconnect();
  }, [ref]);
  return on;
}

// the repo mark after a code project's name (the branching cell, quiet ink)
const NAME_MARK: React.CSSProperties = {
  display: "inline-flex",
  verticalAlign: "-0.1em",
  marginLeft: 4,
  color: "var(--ink-3)",
  lineHeight: 0,
  flex: "none",
};
function NameMark({ node }: { node: OsNode }) {
  // founders only while the repo surface is gated (the shell decides)
  const { showCodeMarks } = useOsShell();
  if (!node.code || !showCodeMarks) return null;
  return (
    <span aria-hidden style={NAME_MARK}>
      <RepoMark solid size=".85em" />
    </span>
  );
}

export type ContextAction = "new-folder" | "new-file" | "move" | "settings" | "park" | "icon";

// small line glyphs for the context menu
const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const CtxIcons = {
  rename: (<svg viewBox="0 0 24 24" {...stroke}><path d="M4 20h4L18.4 9.6a2 2 0 0 0-2.8-2.8L5 17.4 4 20Z" /><path d="M13.6 7.6l2.8 2.8" /></svg>),
  open: (<svg viewBox="0 0 24 24" {...stroke}><path d="M3.5 7.5l1.6-2h4l1.6 2H20a1 1 0 0 1 1 1V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7.5Z" /></svg>),
  newFolder: (<svg viewBox="0 0 24 24" {...stroke}><path d="M3.5 7l1.6-2h4l1.6 2H20a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7Z" /><path d="M12 10.5v5M9.5 13h5" /></svg>),
  newFile: (<svg viewBox="0 0 24 24" {...stroke}><path d="M6 3h7l5 5v13H6V3Z" /><path d="M13 3v5h5" /><path d="M12 12v5M9.5 14.5h5" /></svg>),
  move: (<svg viewBox="0 0 24 24" {...stroke}><path d="M12 3v18M3 12h18" /><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" /></svg>),
  settings: (<svg viewBox="0 0 24 24" {...stroke}><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>),
  park: (<svg viewBox="0 0 24 24" {...stroke}><path d="M9.5 7v10M14.5 7v10" /></svg>),
  select: (<svg viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="8" /><path d="M8.5 12.2l2.4 2.4 4.6-5" /></svg>),
  sendHome: (<svg viewBox="0 0 24 24" {...stroke}><path d="M4 11.5 12 5l8 6.5" /><path d="M6 10.5V19h12v-8.5" /></svg>),
  hideHome: (<svg viewBox="0 0 24 24" {...stroke}><path d="M3 3l18 18" /><path d="M10.6 6.1A9.7 9.7 0 0 1 12 6c5 0 8.5 6 8.5 6a16 16 0 0 1-2 2.6M5.5 8.2A15.6 15.6 0 0 0 3.5 12S7 18 12 18a8.8 8.8 0 0 0 2.8-.45" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>),
  emoji: (<svg viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="8.5" /><path d="M8.8 14.2a4.2 4.2 0 0 0 6.4 0" /><path d="M9.3 9.8h.01M14.7 9.8h.01" /></svg>),
};

// the inline rename field a context-menu "rename" opens, uniform across every view.
function RenameField({ initial, onCommit, onCancel }: { initial: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(initial);
  return (
    <input
      className="ctx-rename"
      autoFocus
      value={v}
      size={Math.max(4, v.length)}
      onChange={(e) => setV(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onCommit(v); }
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={() => onCommit(v)}
      aria-label="rename"
    />
  );
}

interface LayoutApi {
  state: LayoutState;
  // NOTE: no setPosition. The free view arranges itself (see the layout law at
  // the top of this file) — a folder's spot is computed, never stored.
  setSummary: (key: string, text: string) => void;
}

// renameable = anything with a real backing row (not the synthetic type buckets).
const canRename = (c: OsNode) => !!c.projectId && !c.key.startsWith("grp:");

// true when the user asked for reduced motion — glides and pulses collapse to instant.
const reducedMotionNow = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// same narrow/mobile convention as OsShell + OrbitView — below this, drag-to-
// arrange is disabled (it fights touch scroll) and a tap just opens directly.
const useIsNarrow = () => {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return isNarrow;
};

// the folder element under the pointer (a real folder, never the dragged tile) —
// the move-into target, shared by the free-scatter and grid drags.
function folderUnderPoint(px: number, py: number, selfKey: string): HTMLElement | null {
  for (const el of document.elementsFromPoint(px, py) as HTMLElement[]) {
    const f = el.closest?.("[data-folder]") as HTMLElement | null;
    if (f && f.getAttribute("data-node-key") !== selfKey && f.getAttribute("data-node-icon") === "folder") return f;
  }
  return null;
}

// landing emphasis — a quick pulse on the folder that just ACCEPTED a drop, so the
// commit reads as an event, not a quiet disappearance. Inline WAAPI (no stylesheet
// hook needed): a scale swell + a brief organism-coloured glow composed over the
// element's own transform. `data-drop-landed` rides along for ~600ms in case a
// stylesheet wants to join in later.
function landPulse(el: HTMLElement) {
  el.setAttribute("data-drop-landed", "true");
  window.setTimeout(() => el.removeAttribute("data-drop-landed"), 620);
  if (reducedMotionNow()) return;
  const base = el.style.transform && el.style.transform !== "none" ? `${el.style.transform} ` : "";
  const glow = getComputedStyle(el).getPropertyValue("--organism-color").trim() || "rgba(140, 140, 140, 0.55)";
  try {
    el.animate(
      [
        { transform: `${base}scale(1)`, filter: "brightness(1)" },
        { transform: `${base}scale(1.06)`, filter: `brightness(1.15) drop-shadow(0 6px 18px ${glow})`, offset: 0.32 },
        { transform: `${base}scale(1)`, filter: "brightness(1)" },
      ],
      { duration: 600, easing: "cubic-bezier(0.22, 0.85, 0.3, 1)" },
    );
  } catch {
    /* no WAAPI — the attribute cue is enough */
  }
}

function FolderItem({
  child,
  index,
  pos,
  symbol,
  selected,
  editing,
  onOpen,
  onDropInto,
  onMenu,
  onRenameCommit,
  onRenameCancel,
}: {
  child: OsNode;
  index: number;
  /** the COMPUTED spot — a stage fraction from packLayout. Never hand-set. */
  pos: { x: number; y: number };
  symbol: string;
  selected: boolean;
  editing?: boolean;
  onOpen: () => void;
  // drop ONTO another folder → move this node inside it. Returns true if the move
  // was accepted. Anything else and the tile glides back to its computed spot.
  onDropInto?: (targetKey: string) => boolean;
  onMenu?: (e: React.MouseEvent) => void;
  onRenameCommit?: (v: string) => void;
  onRenameCancel?: () => void;
}) {
  const [dragging, setDragging] = useState(false);
  const tileRef = useRef<HTMLDivElement>(null);
  const hovered = useHl(tileRef);
  const [press, setPress] = useState(false);
  // how far the tile is currently carried from its computed spot. This is the
  // ONLY thing a drag writes, and it always returns to zero — a gesture can
  // move a folder into another folder, never to a coordinate.
  const [lift, setLift] = useState<{ dx: number; dy: number } | null>(null);
  // mobile/narrow viewport: the drag gesture is off (it fights touch scroll and
  // the context menu) — a tap opens directly instead.
  const isNarrow = useIsNarrow();
  const everDragged = useRef(false); // the entrance fade is mount-only; never replay after a grab
  const reduceMotion = useReducedMotion();

  // dwell-to-confirm: while we hover a valid folder, an inner glow grows to solid;
  // a release once it's taken hold commits the move. Shared module so the same
  // confirmation reads identically across every folder-management surface.
  const dropc = useDropConfirm({ holdMs: 950 });

  const bind = useDrag(
    ({ first, last, tap, canceled, movement: [mx, my], xy: [px, py], event, cancel }) => {
      // a right-click (or any non-primary button) must never be swallowed as a
      // drag/tap — it needs to reach the browser's contextmenu → our onMenu
      // handler untouched, so the settings dropdown opens reliably.
      if (first && (event as PointerEvent).button != null && (event as PointerEvent).button !== 0) { cancel(); return; }
      if (first) setPress(true); // the brief "clicked" cue on the label
      if (tap) {
        setPress(false);
        setDragging(false);
        setLift(null);
        // single tap OPENS (reverted from select-then-open — opening should feel direct).
        onOpen();
        return;
      }
      if (first) {
        everDragged.current = true; // the entrance fade must never replay after this
        setDragging(true);
      }
      if (!last) {
        setLift({ dx: mx, dy: my });
        // grow the dwell glow on the folder we're hovering as a move-into target.
        dropc.point(onDropInto ? folderUnderPoint(px, py, child.key) : null);
        return;
      }
      // release — commit only if the glow took hold (dwelled past the gate); a
      // graze across a folder drops the tile home instead of yanking it inside.
      const target = dropc.target();
      const tk = target?.getAttribute("data-node-key");
      const confirmed = !canceled && !!tk && dropc.armed();
      dropc.reset();
      setDragging(false);
      setPress(false);
      // ALWAYS release the lift. A committed move sheds the tile from this level
      // (the shell updates the tree live); anything else glides it home on the
      // transform transition below. Either way nothing is left mid-air.
      setLift(null);
      if (confirmed && tk && onDropInto && onDropInto(tk) && target) {
        landPulse(target); // the folder visibly RECEIVES it
      }
    },
    // mobile/narrow viewports disable the whole gesture (it steals the tap from
    // opening + the context menu from right-click/long-press) — a plain onClick
    // below opens instead.
    { filterTaps: true, pointer: { touch: true }, eventOptions: { passive: true }, enabled: !isNarrow },
  );

  const left = `${(pos.x * 100).toFixed(3)}%`;
  const top = `${(pos.y * 100).toFixed(3)}%`;
  const carry = lift && !reduceMotion ? ` translate(${lift.dx}px, ${lift.dy}px)` : "";
  const lifted = dragging && !reduceMotion; // pick-up weight: slight scale + soft shadow

  return (
    <div
      {...bind()}
      ref={tileRef}
      data-hl-target
      data-folder
      data-node-key={child.key}
      data-node-icon={child.icon}
      data-node-name={child.name}
      data-node-path={child.path}
      data-hint={child.summary?.trim() || (child.icon === "folder" ? "a folder of related memory — click to open, right-click for options, drag onto another folder to move it inside" : "a page — click to read, right-click for options")}
      // on mobile/narrow the drag gesture is disabled (enabled: !isNarrow above),
      // so a plain tap needs its own opener — it's otherwise unreachable, never
      // fighting the context-menu's own onContextMenu handler.
      onClick={isNarrow ? onOpen : undefined}
      onContextMenu={onMenu}
      onPointerLeave={() => setPress(false)}
      className={`diricon-gap${everDragged.current ? "" : " os-enter-soft"}`}
      style={{
        position: "absolute",
        left,
        top,
        transform: `translate(-50%, -50%)${carry}${lifted ? " scale(1.045)" : ""}`,
        filter: lifted ? "drop-shadow(0 14px 22px rgba(0, 0, 0, 0.16))" : "none",
        width: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // desktop keeps this tile's original 0.45rem gap; mobile's larger gap
        // (matching the enlarged --diricon-scale icon) comes from .diricon-gap.
        ["--diricon-gap-base" as string]: "0.45rem",
        cursor: isNarrow ? "pointer" : dragging ? "grabbing" : "grab",
        touchAction: isNarrow ? "pan-y" : "none",
        textAlign: "center",
        zIndex: dragging ? 5 : 1,
        // left/top carry the computed spot and glide when the room re-flows
        // (a resize, a folder leaving). transform carries the DRAG: no
        // transition while the hand holds it, a glide home when it lets go.
        transition: dragging
          ? "filter 0.24s var(--ease-soft)"
          : "left 0.45s var(--ease-drift), top 0.45s var(--ease-drift), transform 0.42s var(--ease-drift), filter 0.24s var(--ease-soft)",
        ["--i" as string]: index,
      } as React.CSSProperties}
    >
      <DirIcon icon={dirMark(child)} symbol={symbol} size={92} active={dragging || hovered || press || selected} color={child.color} />
      {editing ? (
        <RenameField initial={child.name} onCommit={onRenameCommit ?? (() => {})} onCancel={onRenameCancel ?? (() => {})} />
      ) : (
        <span
          className="os-folder-label"
          data-selected={selected || undefined}
          data-active={hovered || dragging || press || undefined}
          data-press={press || undefined}
          style={{ fontSize: "0.98rem" }}
        >
          {child.name}
          <NameMark node={child} />
        </span>
      )}
      <OwnerMark owner={child.owner} />
    </div>
  );
}

// ── whose folder this is ────────────────────────────────────────────────────
// In a shared room four people can each stand a folder called "innernet" beside
// one another. The NAME can't tell them apart, so the person does: their mark
// and their name sit under the label, and the emoji on the folder's face is the
// one they set. Nothing is drawn in your own innernet — everything there is
// yours, and a byline on every folder would be noise.
function OwnerMark({ owner }: { owner?: NonNullable<OsNode["owner"]> | null }) {
  if (!owner) return null;
  return (
    <span className="os-owner-mark" aria-label={`kept by ${owner.name ?? "a member"}`}>
      <CellAvatar name={owner.avatarName ?? owner.name ?? "?"} src={owner.avatarUrl} size={16} fontSize="0.55rem" />
      <span className="os-owner-name">{owner.name ?? "a member"}</span>
    </span>
  );
}

// how far a press travels before a grid tile lifts into a drag (px). Below this a
// press is just a click — and on touch, a vertical swipe stays a SCROLL.
const GRID_SLOP = 6;
const GRID_REST = { y: 0, scale: 1 } as const;

// A grid tile: click opens, but past the slop it lifts into a pointer-following
// ghost. The tile ITSELF elevates (transform only — never layout, so the grid
// can't reflow) while a dimmed stand-in holds its slot open. The same
// dwell-to-confirm glow as the free scatter moves it into folders; a release the
// glow didn't bless springs it straight back to the slot.
function GridTile({
  child,
  index,
  symbol,
  selected,
  name,
  onOpen,
  onMenu,
  onDropInto,
}: {
  child: OsNode;
  index: number;
  symbol: string;
  selected: boolean;
  name: React.ReactNode; // the parent's renderName output (inline rename included)
  onOpen: () => void;
  onMenu: (e: React.MouseEvent) => void;
  onDropInto?: (targetKey: string) => boolean;
}) {
  const moveRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const hl = useHl(tileRef);
  const [dragging, setDragging] = useState(false);
  const dragEndAt = useRef(0); // a drag's trailing click must not open
  const reduceMotion = useReducedMotion();
  const hoverLift = useHoverFeel("lift");
  const dropc = useDropConfirm({ holdMs: 950 });
  // mobile/narrow viewport: drag-to-arrange is disabled (it fights touch scroll
  // and the context menu) — the native onClick below still opens directly.
  const isNarrow = useIsNarrow();

  const bind = useDrag(
    ({ first, last, tap, canceled, movement: [mx, my], xy: [px, py], event, cancel }) => {
      if (tap) return; // the native click opens — it also respects the name's rename zone
      if (first) {
        // a right-click must never be swallowed as a drag — let it reach
        // onContextMenu untouched so the settings dropdown opens reliably.
        if ((event as PointerEvent).button != null && (event as PointerEvent).button !== 0) { cancel(); return; }
        // never lift out of an inline rename — selecting text there is a drag too.
        const t = event.target as HTMLElement | null;
        if (t?.closest?.("input, textarea")) { cancel(); return; }
        setDragging(true);
      }
      const el = moveRef.current;
      if (!last) {
        if (el) el.style.transform = `translate(${mx}px, ${my}px)${reduceMotion ? "" : " scale(1.05)"}`;
        // grow the dwell glow on the folder we're hovering as a move-into target.
        dropc.point(onDropInto ? folderUnderPoint(px, py, child.key) : null);
        return;
      }
      // release — same commit gate as the free scatter: only a dwelled glow moves it.
      const target = dropc.target();
      const tk = target?.getAttribute("data-node-key");
      const confirmed = !canceled && !!tk && dropc.armed();
      dropc.reset();
      setDragging(false);
      dragEndAt.current = performance.now();
      if (confirmed && tk && onDropInto && onDropInto(tk)) {
        if (target) landPulse(target); // the folder visibly RECEIVES it
        if (el) el.style.transform = ""; // the tile leaves this level on refresh
        return;
      }
      // not accepted — spring back to the slot, quick and matter-of-fact.
      if (el) {
        const from = el.style.transform;
        el.style.transform = "";
        if (from && !reduceMotion) {
          try {
            el.animate(
              [{ transform: from }, { transform: "translate(0px, 0px) scale(1)" }],
              { duration: 200, easing: "cubic-bezier(0.22, 0.85, 0.3, 1)" },
            );
          } catch {
            /* no WAAPI — the instant return reads fine */
          }
        }
      }
    },
    // threshold = the slop; touch-action stays pan-y below so a vertical touch
    // swipe scrolls the grid — only a deliberate pull becomes a lift. On mobile/
    // narrow viewports the whole gesture is disabled — a tap opens directly via
    // the plain onClick below, and right-click/long-press reaches onContextMenu
    // unobstructed instead of racing the drag binding's pointer capture.
    { filterTaps: true, threshold: GRID_SLOP, pointer: { touch: true }, eventOptions: { passive: true }, enabled: !isNarrow },
  );

  return (
    <div
      {...bind()}
      ref={tileRef}
      data-hl-target
      role="button"
      tabIndex={0}
      onClick={() => {
        if (performance.now() - dragEndAt.current < 420) return; // that click was a drag's tail
        onOpen();
      }}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      onContextMenu={onMenu}
      data-noswipe
      data-folder
      data-node-key={child.key}
      data-node-icon={child.icon}
      data-node-name={child.name}
      data-node-path={child.path}
      data-hint={`${child.name} — ${child.icon === "folder" ? "click to open" : "click to read"} · right-click for options · drag onto a folder to move it`}
      className="os-enter"
      style={{
        position: "relative",
        display: "flex",
        background: "none",
        border: "none",
        padding: 0,
        cursor: dragging ? "grabbing" : "pointer",
        fontFamily: "inherit",
        touchAction: "pan-y",
        zIndex: dragging ? 40 : undefined,
        ["--i" as string]: index,
      } as React.CSSProperties}
    >
      {dragging && (
        // the vacated slot: a dimmed stand-in so the grid reads "it came from here".
        <span
          aria-hidden
          className="diricon-gap"
          style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", opacity: 0.35, pointerEvents: "none" }}
        >
          <DirIcon icon={dirMark(child)} symbol={symbol} size={84} color={child.color} />
          <span style={{ fontSize: "0.94rem", color: "var(--ink)", lineHeight: 1.15 }}>{child.name}</span>
        </span>
      )}
      <div
        ref={moveRef}
        style={{
          position: "relative",
          width: "100%",
          zIndex: dragging ? 41 : undefined,
          filter: dragging && !reduceMotion ? "drop-shadow(0 14px 22px rgba(0, 0, 0, 0.16))" : undefined,
          willChange: dragging ? "transform" : undefined,
        }}
      >
        <motion.span
          // the lift follows the tracker's mark (never framer's own hover, which
          // stuck when the grid re-flowed under a still pointer); the press stays.
          animate={hl && !dragging && hoverLift.whileHover ? hoverLift.whileHover : GRID_REST}
          whileTap={hoverLift.whileTap}
          transition={hoverLift.transition}
          className="diricon-gap"
          style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: "100%" }}
        >
          <DirIcon icon={dirMark(child)} symbol={symbol} size={84} active={dragging} color={child.color} />
          <span
            className="os-selectable"
            data-selected={selected || undefined}
            style={{ fontSize: "0.94rem", color: "var(--ink)", lineHeight: 1.15 }}
          >
            {name}
            <NameMark node={child} />
          </span>
          <OwnerMark owner={child.owner} />
        </motion.span>
      </div>
    </div>
  );
}

export default function DirectoryView({
  node,
  view,
  layout,
  onOpen,
  selectedKey,
  onSelect,
  onRename,
  onMoveInto,
  onContextAction,
  sort,
}: {
  node: OsNode;
  view: string; // home(free) | grid | list
  /** how the grid and the list are ordered (lib/os/folderSort); the default when absent */
  sort?: FolderSort;
  layout: LayoutApi;
  onOpen: (child: OsNode) => void;
  selectedKey?: string | null;
  onSelect?: (key: string) => void;
  onRename?: (child: OsNode, name: string) => void;
  // move a node INTO a folder (drag-and-drop). Returns true if accepted.
  onMoveInto?: (source: OsNode, dest: OsNode) => boolean;
  // new file/folder/move requested from the context menu (creation is server-side).
  onContextAction?: (action: ContextAction, node: OsNode) => void;
}) {
  // ── SELECT, THEN FORGET MANY ─────────────────────────────────────────────
  // "select…" in the right-click menu turns clicks into picks: pages inside a
  // project, or whole projects at a level that holds them, never both at once.
  // The bar's pill opens BatchForgetSheet, which lists every item that would go
  // and asks a second time. Forgotten tiles dissolve, then leave this level.
  type PickKind = "pages" | "projects";
  const pickKindOf = (c: OsNode): PickKind | null => {
    if (c.owner) return null; // someone else's, in a shared room
    if (c.kind === "project" && c.projectId) return "projects";
    if (c.key.startsWith("dim:") && c.editKey && c.projectId) return "pages";
    return null;
  };
  const [pickMode, setPickMode] = useState<PickKind | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const [forgetOpen, setForgetOpen] = useState(false);
  const pickRoot = useRef<HTMLDivElement | null>(null);
  const children = (node.children ?? []).filter((c) => !gone.has(c.key));
  const glow = useGlow();
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState({ w: 1, h: 1 });

  // drag a folder onto another folder → move it inside. Resolve the target key to
  // the node and hand it to the shell, which does the move + refresh.
  const tryMoveInto = (source: OsNode, targetKey: string): boolean => {
    const dest = children.find((c) => c.key === targetKey);
    if (!dest || dest.key === source.key || dest.icon !== "folder" || !onMoveInto) return false;
    return onMoveInto(source, dest);
  };

  // THE arrangement for the free view — computed from the children and the stage,
  // never read from storage. See the layout law at the top of this file: this is
  // the whole anti-collision story, and it holds on first paint, after a resize,
  // and on a screen the room has never been seen on before.
  const packed = useMemo(
    () => packLayout(children.map((c) => c.key), Math.max(1, stage.w), Math.max(1, stage.h)),
    [children, stage.w, stage.h],
  );

  // Measure the stage's WIDTH from the element and take its height from the
  // page's view (the stage's scroller, else the viewport) rather than from the
  // element: the element's own height is what the layout sets, so observing it
  // would feed the pack its own output and the room would creep taller on every
  // pass.
  useIso(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () =>
      setStage({ w: el.clientWidth, h: Math.max(360, Math.round(stageViewport().height * 0.62)) });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [view]);

  useEffect(() => {
    setPickMode(null);
    setPicked(new Set());
    setForgetOpen(false);
  }, [node.key]);
  useEffect(() => {
    if (!pickMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !forgetOpen) { setPickMode(null); setPicked(new Set()); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pickMode, forgetOpen]);
  const startPick = (kind: PickKind, first?: OsNode) => {
    setPickMode(kind);
    setPicked(new Set(first ? [first.key] : []));
  };
  const togglePick = (c: OsNode) => {
    if (!pickMode || pickKindOf(c) !== pickMode) return;
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(c.key)) next.delete(c.key); else next.add(c.key);
      return next;
    });
  };
  const openOrPick = (c: OsNode) => (pickMode ? togglePick(c) : onOpen(c));
  const pickedNodes = children.filter((c) => picked.has(c.key));
  const pickables = children.filter((c) => pickKindOf(c) === pickMode);
  // the tiles are drawn by three different views; mark them from one place.
  //
  // This used to run after EVERY render with a `children.find` per tile (O(N²)),
  // on a level with a hundred pages, on every keystroke anywhere above it. Now it
  // runs when the marks can have changed — the mode, the picks, the tiles, the
  // view — and does nothing at all while no pick has ever begun (the usual case).
  // The tiles by key are a Map, so a pass is O(N).
  const marked = useRef(false);
  const tilesByKey = useMemo(() => new Map((node.children ?? []).map((c) => [c.key, c] as const)), [node.children]);
  useIso(() => {
    const root = pickRoot.current;
    if (!root) return;
    if (!pickMode && !marked.current) return;
    marked.current = !!pickMode;
    if (pickMode) root.setAttribute("data-pick-mode", ""); else root.removeAttribute("data-pick-mode");
    root.querySelectorAll<HTMLElement>("[data-node-key]").forEach((el) => {
      const k = el.getAttribute("data-node-key") ?? "";
      const c = tilesByKey.get(k);
      el.toggleAttribute("data-picked", !!pickMode && picked.has(k));
      el.toggleAttribute("data-unpickable", !!pickMode && (!c || pickKindOf(c) !== pickMode));
      if (pickMode) el.setAttribute("aria-pressed", String(picked.has(k))); else el.removeAttribute("aria-pressed");
    });
  }, [pickMode, picked, tilesByKey, gone, view]);
  const forgetTarget: BatchForgetTarget | null = (() => {
    if (!pickMode || !pickedNodes.length) return null;
    if (pickMode === "projects") return { kind: "projects", projectIds: pickedNodes.map((c) => c.projectId!) };
    const pid = pickedNodes[0].projectId!;
    return { kind: "pages", projectId: pid, names: pickedNodes.filter((c) => c.projectId === pid).map((c) => c.editKey!) };
  })();
  const onForgotten = (count: number) => {
    const names = pickedNodes.map((c) => c.name);
    const keys = pickedNodes.map((c) => c.key);
    setForgetOpen(false);
    setPickMode(null);
    setPicked(new Set());
    // netti's hands: the tiles dissolve where they sit and the shell re-pulls.
    window.dispatchEvent(new CustomEvent("innernet:netti-acts", { detail: { acts: names.map((name) => ({ kind: "delete", name })) } }));
    window.dispatchEvent(new CustomEvent("innernet:clip", {
      detail: { action: "note", name: `forgot ${count} ${pickMode === "projects" ? (count === 1 ? "project" : "projects") : (count === 1 ? "page" : "pages")}` },
    }));
    window.setTimeout(() => setGone((g) => new Set([...g, ...keys])), 420);
  };
  const noun = pickMode === "projects" ? "project" : "page";
  const pickBar =
    pickMode && typeof document !== "undefined"
      ? createPortal(
          <div className="pick-bar" role="toolbar" aria-label={`select ${noun}s to forget`}>
            <span className="pick-bar__count">
              {picked.size ? `${picked.size} ${noun}${picked.size === 1 ? "" : "s"} selected` : `pick the ${noun}s to forget`}
            </span>
            {pickables.length > 0 && (
              <button
                type="button"
                className="quiet-link"
                onClick={() => setPicked(picked.size === pickables.length ? new Set() : new Set(pickables.map((c) => c.key)))}
              >
                {picked.size === pickables.length ? "none" : "all"}
              </button>
            )}
            <button type="button" className="quiet-link" onClick={() => { setPickMode(null); setPicked(new Set()); }}>
              cancel
            </button>
            <PillButton onClick={() => setForgetOpen(true)} disabled={!picked.size}>
              forget…
            </PillButton>
          </div>,
          document.body,
        )
      : null;
  const pickLayer = (
    <>
      {pickBar}
      {forgetOpen && forgetTarget && (
        <BatchForgetSheet target={forgetTarget} onClose={() => setForgetOpen(false)} onForgotten={onForgotten} />
      )}
    </>
  );

  const summaryOf = (c: OsNode) => layout.state.summaries?.[c.key];
  const symbolOf = (c: OsNode) => layout.state.symbols?.[c.key] ?? c.symbol;
  void onSelect; // selection-on-click reverted (single click now opens); kept for the shell's prop

  // a name: editable in place when it backs a real row, else plain text.
  const Name = (c: OsNode, style: React.CSSProperties) =>
    onRename && canRename(c) ? (
      <EditableName value={c.name} onSave={(name) => onRename(c, name)} style={style} title="double-click to rename" />
    ) : (
      <span style={style}>{c.name}</span>
    );

  // right-click menu + the rename it opens (works in every view, incl. free-scatter).
  const { menu, open, close } = useContextMenu();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const commitRename = (c: OsNode, v: string) => {
    const t = v.trim();
    if (t && t !== c.name) onRename?.(c, t);
    setEditingKey(null);
  };
  const renderName = (c: OsNode, style: React.CSSProperties) =>
    editingKey === c.key && onRename && canRename(c)
      ? <RenameField initial={c.name} onCommit={(v) => commitRename(c, v)} onCancel={() => setEditingKey(null)} />
      : Name(c, style);
  const menuFor = (c: OsNode): MenuItem[] => {
    const renameable = !!onRename && canRename(c);
    const act = onContextAction;
    const isProject = c.kind === "project" && !!c.projectId;
    const inProject = isProject || !!c.projectId; // a page can host a new sibling page
    const items: MenuItem[] = [];
    if (isProject && act) items.push({ label: "Settings", icon: CtxIcons.settings, onSelect: () => act("settings", c) });
    items.push(
      { label: "Rename", icon: CtxIcons.rename, disabled: !renameable, onSelect: () => setEditingKey(c.key) },
      { label: "Icon…", icon: CtxIcons.emoji, disabled: !act, onSelect: () => act?.("icon", c) },
      { label: "Open", icon: CtxIcons.open, onSelect: () => onOpen(c) },
      { label: "New folder", icon: CtxIcons.newFolder, separated: true, onSelect: () => act?.("new-folder", c) },
      { label: "New page", icon: CtxIcons.newFile, disabled: !inProject, onSelect: () => act?.("new-file", c) },
      { label: "Move…", icon: CtxIcons.move, onSelect: () => act?.("move", c) },
    );
    if (isProject && act) {
      // shelve/unshelve a whole project — set apart from the everyday verbs, never destructive.
      const parked = (c as OsNode & { parked?: boolean }).parked; // sibling is landing OsNode.parked; drop the cast once it's typed
      items.push({ label: parked ? "Unpark" : "Park", icon: CtxIcons.park, separated: true, onSelect: () => act("park", c) });
    }
    const pk = pickKindOf(c);
    if (pk) items.push({ label: "Select…", icon: CtxIcons.select, separated: !(isProject && act), onSelect: () => startPick(pk, c) });
    return items;
  };
  // a right-click on EMPTY space acts on the current container (this directory):
  // new top-level folder at the root, or a new folder/page inside an open project.
  const containerMenu = (): MenuItem[] => {
    const act = onContextAction;
    const inProject = !!node.projectId;
    const kinds = children.map(pickKindOf).filter((k): k is PickKind => !!k);
    const pk: PickKind | null = kinds.includes("pages") ? "pages" : kinds.includes("projects") ? "projects" : null;
    return [
      { label: "New folder", icon: CtxIcons.newFolder, onSelect: () => act?.("new-folder", node) },
      { label: "New page", icon: CtxIcons.newFile, disabled: !inProject, onSelect: () => act?.("new-file", node) },
      ...(pk ? [{ label: pk === "pages" ? "Select pages…" : "Select projects…", icon: CtxIcons.select, separated: true, onSelect: () => startPick(pk) } as MenuItem] : []),
    ];
  };
  const onContainerMenu = (e: React.MouseEvent) => { if (onContextAction || children.some(pickKindOf)) open(e, containerMenu()); };
  const onMenu = (c: OsNode) => (e: React.MouseEvent) => { e.stopPropagation(); open(e, menuFor(c)); };

  if (children.length === 0) {
    return (
      <div className="os-level" style={{ color: "var(--ink-3)", fontSize: "0.95rem", maxWidth: 460, paddingTop: "2rem" }}>
        nothing here yet.
      </div>
    );
  }

  // ---- list ----
  // The rich list rows (Figma): leading folder/page icon · name + relative time
  // on the head line · a two-line summary beneath. Denser than grid, richer
  // than the old name-only rows.
  //
  // ORDER (John, 18 oct): the grid and the list both follow the viewer's sort
  // (lib/os/folderSort: the `sorted by` line under the chips, turned over by
  // pressing the selected chip). Without one, last touched, newest first:
  // every row wears its relative time, so any other default reads as broken.
  // Undated children sink to the end either way; ties fall back to the name.
  if (view === "list") {
    const byNewest = sortNodes(children, sort ?? DEFAULT_SORT);
    return (
      <div ref={pickRoot} className="list" style={{ maxWidth: 680 }} onContextMenu={onContainerMenu}>
        {byNewest.map((c, i) => {
          const ov = summaryOf(c);
          const summary = ov?.text ?? c.summary;
          return (
            <div
              key={c.key}
              role="button"
              tabIndex={0}
              onClick={() => openOrPick(c)}
              onKeyDown={(e) => { if (e.key === "Enter") openOrPick(c); }}
              onContextMenu={onMenu(c)}
              onPointerMove={glow}
              data-noswipe
              data-node-key={c.key}
              data-node-name={c.name}
              data-node-path={c.path}
              data-selected={selectedKey === c.key || undefined}
              data-hint={c.summary?.trim() || (c.icon === "folder" ? "a folder of related memory — click to open, double-click the name to rename" : "a page to read")}
              className="os-enter list-row list-row--rich"
              style={{ ["--i" as string]: i, cursor: "pointer" } as React.CSSProperties}
            >
              <DirIcon icon={dirMark(c)} symbol={symbolOf(c)} size={56} color={c.color} />
              <div className="list-rich__main">
                <div className="list-rich__head">
                  <span className="list-name">{renderName(c, {})}</span>
                  <NameMark node={c} />
                  <OwnerMark owner={c.owner} />
                  {c.lastActiveAt && <span className="list-time">{relTime(c.lastActiveAt)}</span>}
                </div>
                {summary ? <p className="list-sum">{summary}</p> : null}
              </div>
            </div>
          );
        })}
        <ContextMenu menu={menu} onClose={close} />
        {pickLayer}
      </div>
    );
  }

  // ---- grid (auto) ----
  if (view === "grid") {
    return (
      <div
        ref={pickRoot}
        onContextMenu={onContainerMenu}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
          gap: "clamp(1.4rem, 3vw, 2.4rem)",
          rowGap: "2rem",
          maxWidth: 940,
        }}
      >
        <DirIconScaleStyle />
        {sortNodes(children, sort ?? DEFAULT_SORT).map((c, i) => (
          <GridTile
            key={c.key}
            child={c}
            index={i}
            symbol={symbolOf(c)}
            selected={selectedKey === c.key}
            name={renderName(c, {})}
            onOpen={() => openOrPick(c)}
            onMenu={onMenu(c)}
            onDropInto={(targetKey) => tryMoveInto(c, targetKey)}
          />
        ))}
        <ContextMenu menu={menu} onClose={close} />
        {pickLayer}
      </div>
    );
  }

  // ---- free (the arranged room) ----
  // The stage takes the height the pack asked for, so a level with many folders
  // gets more room instead of crowding its rows into a fixed box.
  return (
    <div
      ref={(el) => { stageRef.current = el; pickRoot.current = el; }}
      data-os-stage
      onContextMenu={onContainerMenu}
      style={{ position: "relative", width: "100%", height: packed.height, minHeight: "clamp(440px, 62vh, 720px)" }}
    >
      <DirIconScaleStyle />
      {children.map((c, i) => (
        <FolderItem
          key={c.key}
          child={c}
          index={i}
          pos={packed.pos[c.key] ?? { x: 0.5, y: 0.5 }}
          symbol={symbolOf(c)}
          selected={selectedKey === c.key}
          editing={editingKey === c.key}
          onOpen={() => openOrPick(c)}
          onDropInto={(targetKey) => tryMoveInto(c, targetKey)}
          onMenu={onMenu(c)}
          onRenameCommit={(v) => commitRename(c, v)}
          onRenameCancel={() => setEditingKey(null)}
        />
      ))}
      <ContextMenu menu={menu} onClose={close} />
      {pickLayer}
    </div>
  );
}
